import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../api/client";
import { IconLock, IconAlert, IconLoader, IconCheck } from "../components/Icons";
import LanguageFlags from "../components/LanguageFlags";
import logoUrl from "/logo-login.png";

export default function ResetPassword() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (password.length < 8) { setErr(t("auth.passwordTooShort")); return; }
    if (password !== password2) { setErr(t("auth.passwordsMismatch")); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => nav("/login"), 3000);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || t("auth.resetError"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center",
      justifyContent: "center", background: "var(--bg)", padding: 24,
    }}>
      <div style={{ position: "fixed", top: 16, right: 16 }}>
        <LanguageFlags />
      </div>
      <div style={{ width: "100%", maxWidth: 400 }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 28 }}>
          <img src={logoUrl} alt="LuigiPOS" style={{ maxWidth: 160, height: "auto" }} />
        </div>
        <div className="card card-glow" style={{ padding: 32 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{t("auth.resetTitle")}</h1>
          <p style={{ color: "var(--text-secondary)", fontSize: 14, marginBottom: 24 }}>
            {t("auth.resetSubtitle")}
          </p>

          {!token ? (
            <div style={{
              padding: "12px 16px", borderRadius: "var(--radius-sm)",
              background: "var(--red-dim)", color: "var(--red)", fontSize: 14,
            }}>
              {t("auth.resetInvalid")}{" "}
              <Link to="/forgot-password" style={{ color: "inherit", fontWeight: 600 }}>{t("auth.resetNewLink")}</Link>.
            </div>
          ) : done ? (
            <div style={{
              display: "flex", alignItems: "flex-start", gap: 8,
              padding: "12px 16px", borderRadius: "var(--radius-sm)",
              background: "var(--green-dim)", color: "var(--green)", fontSize: 14,
            }}>
              <IconCheck size={18} style={{ flexShrink: 0, marginTop: 1 }} />
              {t("auth.resetDone")}
            </div>
          ) : (
            <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {t("auth.newPasswordMin")} <IconLock size={16} className="muted" />
                </label>
                <input
                  type="password" placeholder="••••••••" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required minLength={8}
                />
              </div>
              <div>
                <label>{t("auth.newPassword2")}</label>
                <input
                  type="password" placeholder="••••••••" value={password2}
                  onChange={(e) => setPassword2(e.target.value)}
                  required minLength={8}
                />
              </div>

              {err && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "10px 14px", borderRadius: "var(--radius-sm)",
                  background: "var(--red-dim)", color: "var(--red)", fontSize: 13,
                }}>
                  <IconAlert size={16} />
                  {err}
                </div>
              )}

              <button type="submit" className="primary" disabled={loading} style={{ marginTop: 4 }}>
                {loading ? <IconLoader size={18} /> : t("auth.changePassword")}
              </button>
            </form>
          )}

          <p style={{ marginTop: 24, textAlign: "center", color: "var(--text-secondary)", fontSize: 14 }}>
            <Link to="/login" style={{ color: "var(--accent)", fontWeight: 600 }}>{t("auth.backToLogin")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
